import React, { FC, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useMutation } from '@apollo/client';
import { Modal } from '../../../dialogs/useModal';
import { useGuideCmsContext } from '../GuideCmsContext';
import { MIGRATE_IEEE_CITATIONS } from '../../guide.graphql';
import { findIeeeCitationCandidates, replaceIeeeLinksInDoc, MigrationSelection } from '../sidebar/references/citationMigration';
import { RichText, RichTextField, htmlIsEmpty } from '../../../components/richText';

export interface MigrateCitationsDialogProps {}

export interface MigrateCitationsDialogResult {
  migratedCount: number;
}

interface RowState {
  headingId: string;
  citationId: string;
  text: string;
  occurrenceCount: number;
  selected: boolean;
}

export const MigrateCitationsDialog: FC<Modal<MigrateCitationsDialogProps, MigrateCitationsDialogResult>> = (modal) => {
  const { close } = modal;
  const { editor, guideId } = useGuideCmsContext();
  const [migrate, { loading }] = useMutation(MIGRATE_IEEE_CITATIONS);

  const initialRows = useMemo<RowState[]>(() => {
    if (!editor) return [];
    return findIeeeCitationCandidates(editor).map((c) => ({
      headingId: c.headingId,
      citationId: crypto.randomUUID(),
      text: c.proposedText,
      occurrenceCount: c.occurrenceCount,
      selected: true,
    }));
  }, [editor]);

  const [rows, setRows] = useState<RowState[]>(initialRows);

  const ticked = rows.filter((r) => r.selected);
  const hasNonEmpty = ticked.some((r) => !htmlIsEmpty(r.text));

  const handleOnClose = () => close();

  const handleMigrate = async () => {
    if (!editor) return;
    const selectedRows = rows.filter((r) => r.selected && !htmlIsEmpty(r.text));
    if (selectedRows.length === 0) return;

    const items = selectedRows.map((r) => ({ id: r.citationId, text: r.text }));
    await migrate({ variables: { input: { id: guideId, items } } });

    const selections: MigrationSelection[] = selectedRows.map((r) => ({
      headingId: r.headingId,
      citationId: r.citationId,
      text: r.text,
    }));
    replaceIeeeLinksInDoc(editor, selections);

    close({ migratedCount: selectedRows.length });
  };

  const updateRow = (headingId: string, patch: Partial<RowState>) => {
    setRows((prev) => prev.map((r) => (r.headingId === headingId ? { ...r, ...patch } : r)));
  };

  return (
    <Dialog open={true} onClose={handleOnClose} disableRestoreFocus maxWidth="md" fullWidth>
      <DialogTitle>Migrér IEEE-referanser til litteraturhenvisninger</DialogTitle>
      <DialogContent>
        {rows.length === 0 ? (
          <Alert severity="success">Ingen IEEE-referanser funnet i dokumentet.</Alert>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Velg hvilke referanser som skal migreres til litteraturhenvisninger. Lenkene i teksten oppdateres automatisk til å peke på
              litteraturhenvisningen med <code>[X]</code>-format.
            </Alert>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Manuelt arbeid</AlertTitle>
              Overskriftene som referansene pekte til blir liggende og må slettes manuelt.
              <br />
              Kun overskrifter som er referert til migreres. Litteraturhenvisninger som ikke er referert til må migreres over manuelt.
            </Alert>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell sx={{ width: 80, whiteSpace: 'nowrap' }}>Antall ref.</TableCell>
                    <TableCell>Foreslått sitattekst</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.headingId} hover>
                      <TableCell padding="checkbox">
                        <Checkbox checked={r.selected} onChange={(e) => updateRow(r.headingId, { selected: e.target.checked })} />
                      </TableCell>
                      <TableCell>{r.occurrenceCount}</TableCell>
                      <TableCell>
                        {r.selected ? (
                          <RichTextField value={r.text} onChange={(html) => updateRow(r.headingId, { text: html })} />
                        ) : (
                          <RichText html={r.text} component="div" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleOnClose}>Avbryt</Button>
        <Button variant="contained" onClick={handleMigrate} disabled={loading || ticked.length === 0 || !hasNonEmpty}>
          Migrér {ticked.length > 0 ? `(${ticked.length})` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
